
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { fetchFullData, getAllStates, getCitiesByState } from "@/data/estados-cidades";
import { ChevronDown } from "lucide-react";
import { normalizeUrl } from "@/lib/utils";

const Cidades = () => {
  const [estados, setEstados] = useState<{ sigla: string; nome: string }[]>([]);
  const [expandedState, setExpandedState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Carregar dados completos primeiro
        const fullData = await fetchFullData();
        console.log("Dados completos carregados:", fullData.length, "estados");
        
        // Depois de carregar os dados completos, obter a lista atualizada de estados
        const allStates = getAllStates();
        console.log("Estados obtidos:", allStates.length, "estados");
        
        setEstados(allStates);
        setIsLoading(false);
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const toggleState = (sigla: string) => {
    if (expandedState === sigla) {
      setExpandedState(null);
    } else {
      setExpandedState(sigla);
    }
  };

  // Debug output
  console.log("Estados renderizados:", estados.length);
  console.log("Estado expandido:", expandedState);

  return (
    <>
      <Helmet>
        <title>SEO Local para Cidades do Brasil | LocalUp</title>
        <meta 
          name="description" 
          content="Serviços de SEO Local para todas as cidades do Brasil. Aumente sua visibilidade no Google e atraia mais clientes da sua região." 
        />
      </Helmet>
      
      <div className="min-h-screen bg-white">
        <Navbar />
        
        <div className="py-16 px-6 md:px-12 bg-gradient-to-br from-white to-seo-light">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-bold mb-6 text-seo-dark">
              SEO Local para todas as cidades do Brasil
            </h1>
            
            <p className="text-xl mb-8 text-gray-700">
              Selecione seu estado e cidade para ver estratégias de SEO personalizadas para sua região.
            </p>
          </div>
        </div>

        <section className="py-16 px-6 md:px-12">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold mb-8 text-center text-seo-dark">
              Selecione um estado
            </h2>
            
            {isLoading ? (
              <div className="flex justify-center py-12">
                <p>Carregando estados e cidades...</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {estados.map(estado => (
                  <Card key={estado.sigla} className="overflow-hidden">
                    <div 
                      className="p-4 bg-gray-50 cursor-pointer flex justify-between items-center"
                      onClick={() => toggleState(estado.sigla)}
                    >
                      <h3 className="text-lg font-semibold">{estado.nome}</h3>
                      <Button variant="ghost" size="sm">
                        <ChevronDown className={`transition-transform ${expandedState === estado.sigla ? 'rotate-180' : ''}`} />
                      </Button>
                    </div>

                    {expandedState === estado.sigla && (
                      <CardContent className="p-4">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                          {getCitiesByState(estado.sigla).map((cidade, index) => (
                            <Link 
                              key={`${estado.sigla}-${cidade}-${index}`}
                              to={`/cidade/local/${normalizeUrl(cidade)}`}
                              className="text-seo-primary hover:text-seo-secondary hover:underline p-1"
                            >
                              {cidade}
                            </Link>
                          ))}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Cidades;
